/**
 * Privacy page - sponsor privacy, anonymity, and audit education.
 * 
 * ACT 8: Explains platform's identity-minimized, anonymity-by-default posture.
 * 
 * Privacy doctrine:
 * - Anonymity-by-default: no legal name, government ID, address collected
 * - Pseudonymous identifiers only (email for auth, not identity)
 * - Discovery cards are identity-minimized (no photos, names, or direct info)
 * - Audit trail exists for safety, not surveillance
 * 
 * TODO(ACT 8): Emit privacy_viewed audit event on page load.
 */
import { useEffect } from 'react';

/**
 * Anonymity section - explains anonymous posture.
 */
function AnonymitySection() {
  return (
    <div className="privacy-section">
      <h2 className="section-title">Anonymity Posture</h2>
      <p className="privacy-text">
        InDeep operates under anonymity-by-default doctrine. We do not collect
        or store your legal name, government ID, physical address, employer
        information, or social media profiles.
      </p>
      <p className="privacy-text">
        Your email serves as a pseudonymous authentication identifier only—it
        is not used to build a real-world identity profile.
      </p>
    </div>
  );
}

/**
 * Identity-minimized discovery section.
 */
function DiscoverySection() {
  return (
    <div className="privacy-section">
      <h2 className="section-title">Identity-Minimized Discovery</h2>
      <p className="privacy-text">
        Candidate discovery cards are designed to be identity-minimized. They
        contain:
      </p>
      <ul className="privacy-list">
        <li>Curated display titles (not real names)</li>
        <li>Interest and compatibility hints (not personal details)</li>
        <li>Availability context (not locations or addresses)</li>
        <li>No photos, social profiles, or direct contact information</li>
      </ul>
      <p className="privacy-text">
        This allows browsing candidates without exposing unnecessary personal
        information to either party.
      </p>
    </div>
  );
}

/**
 * Audit trail section - explains auditability.
 */
function AuditSection() {
  return (
    <div className="privacy-section">
      <h2 className="section-title">Audit Trail</h2>
      <p className="privacy-text">
        We maintain an append-only audit trail for safety and accountability.
        This helps:
      </p>
      <ul className="privacy-list">
        <li>Detect and prevent fraud or abuse</li>
        <li>Support safety investigations when needed</li>
        <li>Improve platform integrity</li>
      </ul>
      <p className="privacy-text">
        Audit events track interaction patterns, not content of private messages.
        The audit trail is not used for surveillance or profiling.
      </p>
      <p className="privacy-note">
        Note: Audit trail exists for safety purposes. It does not provide absolute
        anonymity against lawful requests, safety investigations, or platform
        safety processes.
      </p>
    </div>
  );
}

/**
 * Fingerprinting section - explains anti-fraud context.
 */
function FingerprintSection() {
  return (
    <div className="privacy-section">
      <h2 className="section-title">Device Fingerprinting</h2>
      <p className="privacy-text">
        We may collect a device fingerprint for authentication security and
        anti-fraud purposes. This is:
      </p>
      <ul className="privacy-list">
        <li>Used for auth context, not public identity</li>
        <li>Stored securely and separately from personal data</li>
        <li>Not shared with other users</li>
        <li>Not used for tracking or profiling across sites</li>
      </ul>
      <p className="privacy-text">
        Fingerprints help us detect session hijacking, credential stuffing,
        and other automated attacks without requiring personal identity data.
      </p>
    </div>
  );
}

/**
 * Privacy page component.
 */
export function PrivacyPage() {
  // TODO(ACT 8): Emit privacy_viewed audit event on page load
  
  useEffect(() => {
    // Audit TODO: Emit privacy_viewed event
    // TODO(ACT 8): await emitAuditEvent('privacy_viewed', { ... });
  }, []);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Privacy</h1>
        <p className="page-description">
          How InDeep protects your privacy while maintaining safety.
        </p>
      </div>

      <div className="privacy-content" data-testid="privacy-content">
        <AnonymitySection />
        <DiscoverySection />
        <AuditSection />
        <FingerprintSection />
      </div>

      <style>{`
        .privacy-content {
          margin-top: var(--spacing);
        }
        .privacy-section {
          margin-bottom: var(--spacing-lg);
          padding-bottom: var(--spacing-lg);
          border-bottom: 1px solid var(--color-border);
        }
        .privacy-section:last-child {
          border-bottom: none;
        }
        .section-title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing);
          color: var(--color-text);
        }
        .privacy-text {
          font-size: 0.9rem;
          color: var(--color-text-muted);
          line-height: 1.6;
          margin-bottom: var(--spacing);
        }
        .privacy-list {
          list-style: none;
          padding-left: 0;
          margin: var(--spacing) 0;
        }
        .privacy-list li {
          color: var(--color-text-muted);
          font-size: 0.9rem;
          padding: 6px 0;
          padding-left: 20px;
          position: relative;
        }
        .privacy-list li::before {
          content: "•";
          position: absolute;
          left: 4px;
          color: var(--color-accent);
        }
        .privacy-note {
          margin-top: var(--spacing);
          padding: var(--spacing);
          background: var(--color-warning-bg, #fff3e0);
          border-radius: var(--radius);
          font-size: 0.8rem;
          color: var(--color-warning, #e65100);
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
}
